import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Text,
  VStack,
  HStack,
  Divider,
  GridItem,
  useDisclosure,
} from '@chakra-ui/react';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { CloseIcon } from '@chakra-ui/icons';
import { useForm, useWatch } from 'react-hook-form';

//common components
import FormButton from '../../../common/FormButton';
import DialogueBox from '../../../common/DialogueBox';
import SearchSection from '../../../common/SearchSection';
import AssignCertificate from '../../commonSections/AssignCertificate';
import MembershipFormWrapper from '../../../common/membershipFormComponents/MembershipFormWrapper';

//form sections
import DocumentSection, {
  documentInfoFieldsValidation,
} from '../../commonSections/DocumentSection';
import YarnOtherInfoSection, {
  yarnOtherInfoFieldsValidation,
} from '../Verification/YarnOtherInfoSection';
import AdditionalInfoSection, {
  additionalInfoFieldsValidation,
} from '../Verification/AdditionalInfoSection';
import FabricOtherInfoSection, {
  fabricOtherInfoFieldsValidation,
} from '../Verification/FabricOtherInfoSection';
import ProductTypeSection, {
  productionProductTypeFieldsValidation,
} from '../../commonSections/ProductTypeSection';
import GeneralInfoSection, {
  gspGenInfoFieldsValidation,
} from '../../commonSections/GeneralInfoSection';
import ProductInformation, {
  productInfoArrayFieldsValidation,
} from '../../commonSections/ProductInformation';
import SelectCertificateSection from '../../commonSections/SelectCertificateSection';
import MillInformationSection, { millInfoFieldsValidation } from './MillInformationSection';
import IssueDateSection, { issueDateValidation } from '../../commonSections/IssueDateSection';
import YarnCountSection, { yarnsCountsFieldsValidation } from '../Verification/YarnCountSection';

//actions & api
import {
  getAllApplications,
  applyForCertificate,
  getUnusedCertificates,
  assignPreSoldCertificate,
  getMillAnnualProductionCap,
} from '../../../../api/certificates';
import { notify } from '../../../../store/actions/globalActions';
import { FETCH_GSP_APPLICATIONS } from '../../../../store/actions/actionTypes';

//utils & constants
import _ from 'lodash';
import { formatDate } from '../../../../util/formatUtils';
import { useYupValidationResolver } from '../../../../util/yupUtils';

const ApplyForGsp = () => {
  const millErrorRef = useRef();
  const dispatch = useDispatch();
  const [modal, setModal] = useState();
  const assignCertificateRef = useRef();
  const [product, setProduct] = useState();
  const [formData, setFormData] = useState();
  const [capacity, setCapacity] = useState();
  const [certificate, setCertificate] = useState();
  const [selectedMill, setSelectedMill] = useState();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [certErrorMsg, setCertErrorMsg] = useState('');
  const [millUnusedCerts, setMillUnusedCerts] = useState();
  const [certificateToggle, setCertificateToggle] = useState(false);
  const [modalButtonLoading, setModalButtonLoading] = useState(false);
  const user = useSelector((state) => state.auth.user);

  const fetchUnusedCertificates = async () => {
    let certificates = [];
    setMillUnusedCerts();
    const res = await getUnusedCertificates({ type: 'production', millId: selectedMill?.id });
    if (res.data?.status === 200) {
      res.data?.certificates?.map((cert) => {
        if (cert?.type === 'GSP') {
          certificates.push(cert);
        }
      });
      setMillUnusedCerts(certificates);
    }
  };

  const fetchProductionCapacity = async () => {
    const res = await getMillAnnualProductionCap(selectedMill?.id);
    if (res.data?.status === 200) {
      setCapacity(res.data.capacity);
    }
  };

  useEffect(() => {
    if (selectedMill?.id) {
      fetchUnusedCertificates();
      fetchProductionCapacity();
    }
    setCertificate();
    setValue('productType', 'YARN');
  }, [selectedMill?.id, certificateToggle]);

  useEffect(() => {
    if (certificate?.certNum) {
      setCertErrorMsg('');
      setValue('productType', 'YARN');
      if (selectedMill?.categoryId === 1) {
        setValue('productType', 'YARN');
      } else {
        setValue('productType', 'FABRIC');
      }
    } else {
      setCertErrorMsg('Please Select a Certificate');
    }
  }, [certificate]);

  const commonValidationSchema = {
    ...issueDateValidation,
    ...millInfoFieldsValidation,
    ...gspGenInfoFieldsValidation,
    ...documentInfoFieldsValidation,
    ...additionalInfoFieldsValidation,
    ...productInfoArrayFieldsValidation,
    ...productionProductTypeFieldsValidation,
  };

  const getValidationSchema = () => {
    switch (product) {
      case 'YARN':
        return yup.object().shape({
          ...commonValidationSchema,
          ...yarnsCountsFieldsValidation,
          ...yarnOtherInfoFieldsValidation,
        });
      case 'FABRIC':
        return yup.object().shape({
          ...commonValidationSchema,
          ...fabricOtherInfoFieldsValidation,
        });
      default:
        return yup.object().shape({
          ...commonValidationSchema,
        });
    }
  };

  const resolver = useYupValidationResolver(getValidationSchema());

  const {
    reset,
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver,
    defaultValues: { issueDate: new Date() },
  });

  const productType = useWatch({ control, name: 'productType' });

  useEffect(() => {
    setProduct(productType);
  }, [productType]);

  useEffect(() => {
    let capacityString = '';
    if (capacity?.yarnProdCap && capacity?.yarnProdCap !== '0') {
      capacityString += 'Yarn: ' + capacity?.yarnProdCap;
    }
    if (capacity?.knitProdCap && capacity?.knitProdCap !== '0') {
      capacityString += capacity?.yarnProdCap && capacity?.yarnProdCap !== '0' ? ', ' : '';
      capacityString += 'Knit: ' + capacity?.knitProdCap;
    }
    if (capacity?.wovenProdCap && capacity?.wovenProdCap !== '0') {
      capacityString +=
        (capacity?.yarnProdCap && capacity?.yarnProdCap !== '0') ||
        (capacity?.knitProdCap && capacity?.knitProdCap !== '0')
          ? ', '
          : '';
      capacityString += 'Woven: ' + capacity?.wovenProdCap;
    }

    setValue('annualProdCapacity', capacityString);
  }, [capacity]);

  const formatProductDetails = (products) => {
    return products?.map((item) => ({
      ...item,
      invoiceDate: formatDate(item?.invoiceDate),
      deliveryDate: formatDate(item?.deliveryDate),
    }));
  };

  const onSubmit = (data) => {
    let formdata = {
      ...data,
      millId: selectedMill?.id,
      certId: certificate?.certID,
      dateOfLc: formatDate(data?.dateOfLc),
      issueDate: formatDate(data?.issueDate),
      btbLcDate: formatDate(data?.btbLcDate),
      bank: data?.bank + ',' + data?.bankBranch,
      yarnCount: JSON.stringify(data?.yarnCount),
      expiryDateOfLc: formatDate(data?.expiryDateOfLc),
      additionalInfo: JSON.stringify(data?.additionalInfo),
      exciseGatePassDate: formatDate(data?.exciseGatePassDate),
      productDetails: JSON.stringify(formatProductDetails(data?.productDetails)),
    };

    let normalizedFormData = _.mapKeys(formdata, (value, key) => {
      if (value) {
        return key;
      }
    });

    setFormData(normalizedFormData);
    setModal('CONFIRM_APPLICATION');
    onOpen();
  };

  const confirmSubmit = async () => {
    const res = await applyForCertificate({ type: 'production', formData });
    if (res.data?.status === 200) {
      dispatch(
        notify({
          title: 'Success',
          description: res.data.msg,
          status: 'success',
          duration: 5000,
        }),
      );
      let resetObj = {};
      _.mapKeys(formData, (value, key) => (resetObj[key] = ''));
      reset({
        ...resetObj,
        yarnCount: [],
        additionalInfo: [],
        productDetails: [],
        issueDate: new Date(),
        annualProdCapacity: formData?.annualProdCapacity,
      });
      const response = await getAllApplications({
        type: 'production',
        params: { page: 1, pageSize: 10, type: 'GSP' },
      });
      if (response.data?.status === 200) {
        dispatch({ type: FETCH_GSP_APPLICATIONS, payload: response.data });
      }
      setCertificateToggle(!certificateToggle);
    }
  };

  const handlePreSoldCert = () => {
    setModal('CERT_NUMBER_FORM');
    onOpen();
  };

  const handleAssignCert = async (data) => {
    const res = await assignPreSoldCertificate({
      millId: selectedMill?.id,
      certificateNum: data?.certificateNum,
      type: 'PRODUCTION_GSP',
    });
    if (res.data?.status === 200) {
      dispatch(
        notify({
          title: 'Success',
          description: res.data.msg,
          status: 'success',
          duration: 5000,
        }),
      );
      fetchUnusedCertificates();
      onClose();
    }
  };

  const renderModal = () => {
    switch (modal) {
      case 'CONFIRM_APPLICATION':
        return (
          <DialogueBox
            close={onClose}
            alertTitle="Confirm"
            mountControl={isOpen}
            performAction={confirmSubmit}
            alertMessage="Are you sure you want to submit these information and verify this application?"
          />
        );
      case 'CERT_NUMBER_FORM':
        return (
          <DialogueBox
            close={onClose}
            onActionKeepOpen
            mountControl={isOpen}
            actionButtonText="Submit"
            cancelButtonText="Cancel"
            alertTitle="Assign Pre-sold Certificate"
            actionButtonLoading={modalButtonLoading}
            performAction={() => assignCertificateRef.current.submit()}>
            <AssignCertificate
              certType="GSP"
              mill={selectedMill}
              ref={assignCertificateRef}
              handleQuery={handleAssignCert}
              submitting={setModalButtonLoading}
            />
          </DialogueBox>
        );
    }
  };

  return (
    <Box>
      <SearchSection setSelectedMill={setSelectedMill} />
      <Box mb={2} mt={5} px={4}>
        <Text fontSize="20px" fontWeight="bold" mb={2}>
          Select Mill & Certificate
        </Text>
        <Divider />
      </Box>
      <Grid templateColumns="repeat(5, 1fr)" gap={3} p={4} mx={4} bg="white" borderRadius="4px">
        <GridItem colSpan={2}>
          <Box
            p={4}
            zIndex={1}
            border="1px"
            bg="secondary"
            borderRadius="4px"
            position="relative"
            borderStyle="dashed"
            borderColor="primary.300">
            {selectedMill?.id ? (
              <CloseIcon
                right={3}
                cursor="pointer"
                position="absolute"
                onClick={() => {
                  setSelectedMill();
                  setCertificate();
                }}
              />
            ) : null}
            <Text fontSize="md" color="textSecondary">
              Selected Mill
            </Text>
            <Text fontSize="xl" mt={1.5}>
              {selectedMill?.millName || 'None'}
            </Text>
          </Box>
          {selectedMill ? null : (
            <Text fontSize="sm" color="invalid" ref={millErrorRef} mt={2}>
              Please Select a Mill
            </Text>
          )}
        </GridItem>
        {selectedMill && millUnusedCerts?.length > 0 ? (
          <GridItem colSpan={3}>
            <SelectCertificateSection
              error={certErrorMsg}
              certificates={millUnusedCerts}
              setCertificate={setCertificate}
              defaultValue={certificate?.certID}
            />
          </GridItem>
        ) : selectedMill ? (
          <GridItem
            p={4}
            colSpan={3}
            border="1px"
            bg="secondary"
            borderRadius="4px"
            borderColor="invalid">
            <VStack justifyContent="center" h="100%">
              <Text fontSize="md" color="invalid">
                No Unused Certificate Found for This Mill! The member needs to buy blank certificate
                or admin may assign unused blank certificate previously bought by the member.
              </Text>
            </VStack>
          </GridItem>
        ) : null}
      </Grid>
      {user.adminRole === 'SUPERADMIN' && (
        <>
          {selectedMill && !certificate ? (
            <FormButton ml={4} onClick={handlePreSoldCert}>
              Assign Pre-sold Certificate
            </FormButton>
          ) : null}
        </>
      )}

      {selectedMill && certificate ? (
        <MembershipFormWrapper formName="GSP Application" mt={4}>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem colSpan={2} bg="white" borderRadius="4px" p={4}>
              <ProductTypeSection
                errors={errors}
                control={control}
                type="PRODUCTION"
                register={register}
              />
            </GridItem>
            {productType ? (
              <>
                <GridItem colSpan={2} bg="white" borderRadius="4px" p={4}>
                  <IssueDateSection control={control} register={register} errors={errors} />
                  <GeneralInfoSection
                    type="GSP"
                    errors={errors}
                    control={control}
                    register={register}
                  />
                </GridItem>
                <GridItem colSpan={2} bg="white" borderRadius="4px" p={4}>
                  <DocumentSection
                    type="GSP"
                    errors={errors}
                    control={control}
                    register={register}
                  />
                </GridItem>
                <GridItem colSpan={2} bg="white" borderRadius="4px" p={4}>
                  <ProductInformation control={control} register={register} errors={errors} />
                </GridItem>
                <GridItem colSpan={2} bg="white" borderRadius="4px" p={4}>
                  <MillInformationSection control={control} register={register} errors={errors} />
                </GridItem>
                <GridItem colSpan={2} bg="white" borderRadius="4px" p={4}>
                  {productType === 'YARN' ? (
                    <>
                      <YarnOtherInfoSection register={register} control={control} errors={errors} />
                      <AdditionalInfoSection
                        control={control}
                        register={register}
                        errors={errors}
                      />
                      <YarnCountSection register={register} control={control} errors={errors} />
                    </>
                  ) : (
                    <>
                      <FabricOtherInfoSection
                        register={register}
                        control={control}
                        errors={errors}
                      />
                      <AdditionalInfoSection
                        control={control}
                        register={register}
                        errors={errors}
                      />
                    </>
                  )}
                </GridItem>
              </>
            ) : null}
          </Grid>
          {productType ? (
            <HStack justifyContent="end" alignItems="center" mt={4}>
              <FormButton
                mt={0}
                isLoading={isSubmitting}
                onClick={(event) => {
                  event.preventDefault();
                  handleSubmit(onSubmit)();
                }}>
                SUBMIT
              </FormButton>
            </HStack>
          ) : null}
        </MembershipFormWrapper>
      ) : null}
      {renderModal()}
    </Box>
  );
};

export default ApplyForGsp;
